import React from 'react';

import { connect } from 'react-redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import ErrorMessage from 'common/ErrorMessage';
import useMutation from 'common/hooks/useMutation';
import VStack from 'common/ui/VStack';
import Button from 'common/uiV3/Button/Button';
import ButtonLink from 'common/uiV3/Button/ButtonLink';
import Card from 'common/uiV3/Card';
import Dialog from 'common/uiV3/Dialog';

type ConnectProps = {
  reloadCompany: () => Promise<void>;
};

type OwnProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

type Props = OwnProps & ConnectProps;

const AdminJoinBetaModal = ({ isOpen, onOpenChange, reloadCompany }: Props) => {
  const joinBeta = useMutation<void, { enabled: boolean }>({
    mutationFn: async (input) => AJAX.post('/api/ideas/beta', input),
    onSuccess: async () => {
      await reloadCompany();
      handleOpenChange(false);
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      joinBeta.reset();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content className="gap-0 sm:max-w-[480px]">
        <iframe
          className="aspect-video border-none rounded-t-[11px]"
          allowFullScreen
          src="https://www.loom.com/embed/30acdf6f84b44a5b95b56a0986d25f1b?hideEmbedTopBar=true"
        />
        <VStack className="p-6 gap-2">
          <h2 className="text-3xl font-semibold">Join the Canny Ideas beta</h2>
          <p className="text-base text-secondary-foreground">
            Your team has early access to our newest features!
          </p>
          <Card className="p-4">
            <VStack className="gap-2">
              <h3 className="text-base font-semibold">Benefits of Canny Ideas</h3>
              <ul className="list-disc list-inside space-y-2 text-secondary-foreground">
                <li>Structure and organize feedback using hierarchy</li>
                <li>Customize views to support different workflows</li>
                <li>Separate internal views from the feedback portal</li>
              </ul>
            </VStack>
          </Card>
          <p>
            This requires some setup and it affects all admins. Join now if you're ready or opt-in
            later.{' '}
            <ButtonLink
              href="https://help.canny.io/en/articles/11987265-canny-ideas"
              variant="link">
              Learn more
            </ButtonLink>
          </p>
          {joinBeta.error ? <ErrorMessage>{joinBeta.error.message}</ErrorMessage> : null}
        </VStack>
        <Dialog.Footer className="border-t-0 pt-0">
          <Dialog.Close asChild>
            <Button variant="outlined">Dismiss</Button>
          </Dialog.Close>
          <Button
            variant="contained"
            isLoading={joinBeta.isLoading}
            onClick={() => joinBeta.mutate({ enabled: true })}>
            Join the beta
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

export default connect(null, {
  reloadCompany,
})(AdminJoinBetaModal) as unknown as React.FC<OwnProps>;
